import DownloadIcon from '@material-design-icons/svg/filled/download.svg?react';
import RefreshIcon from '@material-design-icons/svg/filled/refresh.svg?react';
import type { AuditFilters } from '../../stores/auditManagementStore';
import { resetFilters, updateFilter } from '../../stores/auditManagementStore';
import { FilterBar, SelectFilter } from '../common/FilterBar';
import { type Choice, SearchableMultiSelect } from '../SearchableMultiSelect';
import { Button } from '../ui/button';

interface AuditFilterBarProps {
  filters: AuditFilters;
  actionOptions: Choice[];
  entityTypeOptions: Choice[];
  isLoading: boolean;
  hasData: boolean;
  onLoadAudits: () => void;
  onExport: (format: 'csv' | 'json') => void;
}

export const AuditFilterBar = ({
  filters,
  actionOptions,
  entityTypeOptions,
  isLoading,
  hasData,
  onLoadAudits,
  onExport,
}: AuditFilterBarProps) => {
  return (
    <FilterBar
      searchPlaceholder="Search audit logs..."
      searchValue={filters.search}
      onSearchChange={(value) => updateFilter('search', value)}
    >
      <SelectFilter
        value={filters.status}
        onChange={(value) => updateFilter('status', value)}
        options={[
          { value: 'all', label: 'All Statuses' },
          { value: 'success', label: 'Success' },
          { value: 'failure', label: 'Failure' },
          { value: 'pending', label: 'Pending' },
        ]}
      />
      <SearchableMultiSelect
        choices={actionOptions}
        value={filters.actions}
        onChange={(values) => updateFilter('actions', values)}
        placeholder="All Actions"
        searchPlaceholder="Search actions..."
        className="min-w-[200px]"
      />
      <SearchableMultiSelect
        choices={entityTypeOptions}
        value={filters.entityTypes}
        onChange={(values) => updateFilter('entityTypes', values)}
        placeholder="All Entity Types"
        searchPlaceholder="Search entity types..."
        className="min-w-[200px]"
      />
      <div className="flex gap-2">
        <input
          type="date"
          aria-label="Start Date"
          value={filters.startDate}
          onChange={(e) => updateFilter('startDate', e.target.value)}
          className="px-3 py-2 bg-neutral-800 border border-neutral-600 rounded-md text-sm text-white focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-colors"
          placeholder="Start Date"
        />
        <input
          type="date"
          aria-label="End Date"
          value={filters.endDate}
          onChange={(e) => updateFilter('endDate', e.target.value)}
          className="px-3 py-2 bg-neutral-800 border border-neutral-600 rounded-md text-sm text-white focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-colors"
          placeholder="End Date"
        />
      </div>
      <div className="flex gap-2 ml-auto">
        <Button
          variant="outline"
          onClick={resetFilters}
          className="flex items-center gap-2"
        >
          Clear
        </Button>
        <Button
          variant="outline"
          onClick={() => onExport('csv')}
          className="flex items-center gap-2"
          disabled={isLoading || !hasData}
        >
          <DownloadIcon className="fill-current size-4" />
          CSV
        </Button>
        <Button
          variant="outline"
          onClick={() => onExport('json')}
          className="flex items-center gap-2"
          disabled={isLoading || !hasData}
        >
          <DownloadIcon className="fill-current size-4" />
          JSON
        </Button>
        <Button
          variant="outline"
          onClick={onLoadAudits}
          className="flex items-center gap-2"
          disabled={isLoading}
        >
          <RefreshIcon className="fill-current size-4" />
          Refresh
        </Button>
      </div>
    </FilterBar>
  );
};
